import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import difference from 'lodash/difference'
import take from 'lodash/take'
import upperCase from 'lodash/upperCase'

import TooltipA from 'modules/core/components/Tooltip'
import i18n from 'modules/core/lib/i18n'

import CoverageShiftsSummary from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/CoverageShiftsSummary'
import MoreShiftsTooltip from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Header/MoreShiftsTooltip'
import SummaryTotals from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Header/SummaryTotals'
import {
  Column,
  ColumnsType,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import {
  UnassignedShiftSummary,
  useCalculateSummaryFromShifts,
} from 'modules/shift_management/hooks/useCalculateSummaryUnassignedShifts'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import { overrideGammaDimensions } from 'modules/shift_management/store/internal/models/View'

interface Props {
  columns: ColumnsType
}

const CoverageSummary = ({ columns }: Props) => {
  const [{ view }] = useSchedule()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const columnMinWidth = view.columnMinWidth
  const dimensions = overrideGammaDimensions()
  const width = dimensions[columnMinWidth]

  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')

  // TODO: Remove all the following code + its imports once the new entry flow is enabled for everyone and FF is removed ↓
  const [unassignedShifts] = useUnassignedShifts()
  const [isTooltipMoreOpen, toggleTooltipMore] = useToggle(false)

  const { summaryFromShifts } = useCalculateSummaryFromShifts({
    unassignedShifts: unassignedShifts,
  })

  const unassignedShiftsSummary = Array.from(summaryFromShifts.values())
  const unassignedShiftsSummarySorted = unassignedShiftsSummary.sort((a, b) =>
    a.title.localeCompare(b.title)
  )

  const moreThanThreeShifts = unassignedShiftsSummarySorted.length > 3

  const unassignedShiftsToDisplay = moreThanThreeShifts
    ? take(unassignedShiftsSummarySorted, 3)
    : unassignedShiftsSummarySorted

  const remainingShifts = difference(unassignedShiftsSummarySorted, unassignedShiftsToDisplay)

  const moreShiftsTooltip = () => <MoreShiftsTooltip unassignedShiftsSummary={remainingShifts} />

  return !newEntryFlowEnabled ? (
    <Box flexDirection="row" alignItems="center" justifyContent="flexEnd" gap="s12" width="full">
      <Box flexDirection="row" alignItems="center" gap="s12" width="full" justifyContent="flexEnd">
        {unassignedShiftsToDisplay.map((unassignedShiftSummary: UnassignedShiftSummary) => (
          <TooltipA.Alt
            key={unassignedShiftSummary.color.toString()}
            attachment="top center"
            label={unassignedShiftSummary.title + ': ' + unassignedShiftSummary.counter}
            textAlign="center"
          >
            <Box
              key={unassignedShiftSummary.color.toString()}
              flexDirection="row"
              gap="s2"
              alignItems="center"
            >
              <Icon.Small icon="circle" color={unassignedShiftSummary.color} />
              <Box maxWidth="s96">
                <Text size="100" weight="bold" color="grey700" ellipsis overflowWrap>
                  {upperCase(unassignedShiftSummary.title)}:
                </Text>
              </Box>
              <Text size="100" weight="bold" color="grey700">
                {unassignedShiftSummary.counter}
              </Text>
            </Box>
          </TooltipA.Alt>
        ))}

        {moreThanThreeShifts && (
          <Tooltip.Dialog
            attachment="top center"
            open={isTooltipMoreOpen}
            toggle={toggleTooltipMore}
            label={moreShiftsTooltip()}
          >
            <Box alignItems="center">
              <Text size="100" weight="bold" color="grey700" ellipsis>
                {upperCase(i18n.t('common.more'))}
              </Text>
            </Box>
          </Tooltip.Dialog>
        )}
      </Box>
      <SummaryTotals />
    </Box>
  ) : (
    <Box
      style={{
        gridTemplateColumns: `repeat(${view.dates.length}, minmax(${width}, 1fr))`,
      }}
      flexDirection="row"
      height="full"
    >
      {columns.map(
        (heading: Column, index: number) =>
          index !== 0 && <CoverageShiftsSummary key={index} date={heading.date} />
      )}
    </Box>
  )
}

export default CoverageSummary
